import React from "react";
import { useSearchParams } from "react-router-dom";
import { usePermissionLevel } from "../../hooks/usePermissionLevel";
import MembersTab from "./MembersTab";
import PromptFramesTab from "./PromptFramesTab";
import SessionsTab from "./sessions/SessionsTab";
import { parseAdminTab, type AdminTab } from "./sessions/adminSessionsUrl";

export interface AdminPageProps {
  getToken: () => string | undefined;
}

const TABS: { id: AdminTab; label: string }[] = [
  { id: "members", label: "Member Management" },
  { id: "prompts", label: "System Prompts" },
  { id: "sessions", label: "Sessions" },
];

const AdminPage: React.FC<AdminPageProps> = ({ getToken }) => {
  const { isAdmin } = usePermissionLevel();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseAdminTab(searchParams);
  // Switching tabs drops the other tab's params (e.g. the selected user).
  const setTab = (next: AdminTab) => setSearchParams({ tab: next });

  if (!isAdmin) {
    return (
      <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
        <div className="max-w-5xl w-full mx-auto px-8 py-10 flex flex-col items-center justify-center text-center min-h-full">
          <span className="material-symbols-outlined text-5xl text-slate-500 mb-4">
            lock
          </span>
          <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface mb-2">
            Admin access required
          </h2>
          <p className="text-slate-500 font-body text-sm max-w-md">
            You need to be an admin to access this page. Contact an administrator
            in your organization if you believe this is a mistake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-y-auto bg-background">
      <div className="max-w-6xl w-full mx-auto px-8 py-10">
        <div className="flex gap-6 border-b border-outline-variant/30 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`pb-3 -mb-px font-label text-xs uppercase tracking-widest transition-colors border-b-2 ${
                tab === t.id
                  ? "border-primary text-on-surface"
                  : "border-transparent text-slate-500 hover:text-on-surface"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "members" && <MembersTab getToken={getToken} />}
        {tab === "prompts" && <PromptFramesTab getToken={getToken} />}
        {tab === "sessions" && <SessionsTab getToken={getToken} />}
      </div>
    </div>
  );
};

export default AdminPage;
